import { Controller } from '@hotwired/stimulus';
import { showToast } from '../lib/toast';
import { normalize, fuzzyMatch } from '../lib/fuzzy';

// Bulk-edit overlay modal: search, add/remove variable rows and submit them all
// at once via fetch (Rails nested attributes), so the editor content is never
// reloaded. Textareas auto-grow (no inner scroll). Stays in sync with the drawer
// through the window "tpled:variables-synced" event.
export default class extends Controller {
  static targets = [
    'form', 'grid', 'rowTemplate', 'submit', 'submitSpinner', 'submitIcon',
    'search', 'searchClear',
  ];

  connect() {
    this.onKeydown = this.onKeydown.bind(this);
    // Start above the server-rendered rows (indices 0..n-1) so a newly added
    // row never reuses an existing index and overwrites it in the params hash.
    this.newIndex = this.existingRowCount();

    // Opened straight from the template show ("Edit variables" → /edit#variables).
    if (window.location.hash === '#variables') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
      this.open();
    }
  }

  existingRowCount() {
    return this.gridTarget.querySelectorAll('[data-bulk-editor-target="row"]').length;
  }

  disconnect() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  // ---- open / close --------------------------------------------------------

  open() {
    this.element.hidden = false;
    document.addEventListener('keydown', this.onKeydown);
    // scrollHeight is only meaningful once visible: reset filter then size.
    this.resetSearch();
    const firstInput = this.gridTarget.querySelector('.ds-tpled-binp');
    if (firstInput) firstInput.focus();
  }

  close() {
    this.element.hidden = true;
    document.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  backdropClose(event) {
    if (event.target === this.element) this.close();
  }

  // ---- rows ----------------------------------------------------------------

  addRow() {
    // Clear any active filter so the new row is not hidden by the search.
    this.resetSearch();
    const row = this.cloneRow(this.newIndex);
    this.newIndex += 1;
    this.gridTarget.appendChild(row);
    this.sizeRow(row);
    const input = row.querySelector('.ds-tpled-binp');
    if (input) input.focus();
  }

  removeRow(event) {
    const row = event.target.closest('.ds-tpled-bulkrow');
    if (!row) return;

    const idField = row.querySelector('input[name$="[id]"]');
    if (idField && idField.value) {
      const destroy = row.querySelector('[data-bulk-editor-target="destroy"]');
      if (destroy) destroy.value = '1';
      row.dataset.destroyed = 'true';
      row.hidden = true;
    } else {
      row.remove();
    }
  }

  cloneRow(index, variable = null) {
    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);
    row.querySelectorAll('[name]').forEach((field) => {
      field.name = field.name.replace('NEW_RECORD', index);
    });

    if (variable) {
      const idField = document.createElement('input');
      idField.type = 'hidden';
      idField.name = `template[variables_attributes][${index}][id]`;
      idField.value = variable.id;
      row.prepend(idField);
      row.querySelector('[name$="[title]"]').value = variable.title || '';
      row.querySelector('[name$="[description]"]').value = variable.description || '';
      row.querySelector('[name$="[seed_value]"]').value = variable.seed_value || '';
    }
    return row;
  }

  // Rebuild the grid from the authoritative list so reopening after a save does
  // not duplicate just-added rows. Runs on the drawer's sync events.
  onVariablesSynced(event) {
    const variables = event.detail?.variables || [];
    if (!variables.length || variables.some((item) => typeof item === 'string')) {
      return;
    }
    this.gridTarget.replaceChildren();
    variables.forEach((variable, index) => {
      this.gridTarget.appendChild(this.cloneRow(index, variable));
    });
    this.newIndex = variables.length;
    if (!this.element.hidden) {
      this.resetSearch();
    }
  }

  // ---- search & auto-grow --------------------------------------------------

  searchRows() {
    const query = this.hasSearchTarget
      ? normalize(this.searchTarget.value.trim()) : '';
    if (this.hasSearchClearTarget) {
      this.searchClearTarget.hidden = query.length === 0;
    }

    this.gridTarget
      .querySelectorAll('[data-bulk-editor-target="row"]')
      .forEach((row) => {
        if (row.dataset.destroyed === 'true') { row.hidden = true; return; }
        row.hidden = query ? !fuzzyMatch(query, normalize(this.rowText(row))) : false;
      });

    this.autoSizeAll();
  }

  clearSearch() {
    if (this.hasSearchTarget) this.searchTarget.value = '';
    this.searchRows();
    if (this.hasSearchTarget) this.searchTarget.focus();
  }

  resetSearch() {
    if (this.hasSearchTarget) this.searchTarget.value = '';
    this.searchRows();
  }

  rowText(row) {
    const value = (selector) => row.querySelector(selector)?.value || '';
    return [
      value('[name$="[title]"]'),
      value('[name$="[description]"]'),
      value('[name$="[seed_value]"]'),
    ].join(' ');
  }

  autogrow(event) {
    this.sizeTextarea(event.target);
  }

  autoSizeAll() {
    this.gridTarget
      .querySelectorAll('textarea.ds-tpled-binp')
      .forEach((textarea) => this.sizeTextarea(textarea));
  }

  sizeRow(row) {
    row.querySelectorAll('textarea.ds-tpled-binp')
      .forEach((textarea) => this.sizeTextarea(textarea));
  }

  sizeTextarea(textarea) {
    textarea.style.height = 'auto';
    // Add the border delta so content fits exactly under border-box sizing.
    const borders = textarea.offsetHeight - textarea.clientHeight;
    textarea.style.height = `${textarea.scrollHeight + borders}px`;
  }

  // ---- validation ----------------------------------------------------------

  // Mirrors the Variable model rules so erroring fields turn red before the
  // round-trip. Empty rows are ignored (Rails drops all-blank nested records).
  validateRows() {
    const namePattern = /^[a-zA-Z0-9_]+$/;
    let valid = true;

    this.gridTarget
      .querySelectorAll('[data-bulk-editor-target="row"]')
      .forEach((row) => {
        if (row.dataset.destroyed === 'true') return;

        const title = row.querySelector('[name$="[title]"]');
        const description = row.querySelector('[name$="[description]"]');
        const seed = row.querySelector('[name$="[seed_value]"]');
        const fields = [title, description, seed];
        if (fields.every((field) => !field || field.value.trim() === '')) {
          this.mark(title, true);
          this.mark(description, true);
          return;
        }

        const titleOk = namePattern.test((title?.value || '').trim());
        const descriptionOk = (description?.value || '').trim().length > 0;
        this.mark(title, titleOk);
        this.mark(description, descriptionOk);
        if (!titleOk || !descriptionOk) valid = false;
      });

    return valid;
  }

  mark(input, ok) {
    if (input) input.classList.toggle('is-invalid', !ok);
  }

  clearField(event) {
    event.target.classList.remove('is-invalid');
  }

  // ---- submit --------------------------------------------------------------

  submit(event) {
    event.preventDefault();
    if (!this.validateRows()) {
      showToast('error', this.message('error'));
      return;
    }
    this.setSubmitting(true);

    fetch(this.formTarget.action, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken() },
      body: new FormData(this.formTarget),
    })
      .then((response) => this.handleResponse(response))
      .catch(() => {
        this.setSubmitting(false);
        showToast('error', this.message('error'));
      });
  }

  handleResponse(response) {
    this.setSubmitting(false);
    if (response.ok) {
      this.close();
      showToast('success', this.message('success'));
      window.dispatchEvent(new CustomEvent('tpled:variables-changed'));
    } else {
      showToast('error', this.message('error'));
    }
  }

  setSubmitting(submitting) {
    this.submitTarget.disabled = submitting;
    this.submitSpinnerTarget.classList.toggle('hidden', !submitting);
    this.submitIconTarget.classList.toggle('hidden', submitting);
  }

  message(kind) {
    return this.element.dataset[`${kind}Message`] || '';
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content;
  }
}
